import React from "react";
import LegalLayout from "../../components/LegalLayout";
import useDocumentTitle from "../../hooks/useDocumentTitle";
import { Scale, FileSignature, Receipt, Gavel } from "lucide-react";

const SECTIONS = [
  { id: "service",         label: "The Service" },
  { id: "eligibility",     label: "Eligibility & accounts" },
  { id: "acceptable-use",  label: "Acceptable use" },
  { id: "customer-content", label: "Customer content" },
  { id: "ip",              label: "Intellectual property" },
  { id: "fees-billing",    label: "Fees & billing" },
  { id: "term-termination", label: "Term & termination" },
  { id: "confidentiality", label: "Confidentiality" },
  { id: "warranties",      label: "Warranties" },
  { id: "liability",       label: "Limitation of liability" },
  { id: "indemnification", label: "Indemnification" },
  { id: "third-party",     label: "Third-party services" },
  { id: "changes-service", label: "Changes" },
  { id: "governing-law",   label: "Governing law" },
  { id: "misc",            label: "Miscellaneous" },
  { id: "contact",         label: "Contact" },
];

const PILLARS = [
  { icon: Scale,         title: "Plain-English where possible", desc: "We've kept the legalese lean. Where it must stay precise (limitation of liability, governing law), we flag it — no buried hooks.", accent: "var(--qt-cyan)" },
  { icon: FileSignature, title: "Reports are advisory",        desc: "QorTrace findings, scores, and certificates reflect our best technical assessment under our published methodology — not legal, investment, or fiduciary advice.", accent: "var(--qt-amber)" },
  { icon: Receipt,       title: "Predictable billing",         desc: "Subscriptions and per-audit credits are clearly priced. Refunds: pro-rata for downgrades, none for consumed audit credits except in cases of confirmed material fault.", accent: "#1FDD83" },
  { icon: Gavel,         title: "Fair termination",            desc: "Either party can terminate for material breach with 30 days' written notice. Your data is exportable for 90 days after termination, then permanently deleted.", accent: "var(--qt-cyan)" },
];

export default function Terms() {
  useDocumentTitle("Terms of Service — QorTrace");
  return (
    <LegalLayout
      eyebrow="LEGAL · TERMS"
      title="Terms of Service"
      lastUpdated="February 2026"
      version="qortrace-terms-v1.0"
      status="ACTIVE"
      intro="These Terms form a binding agreement between you and QorTrace, governing your access to and use of our web app, APIs, command-line tools, and documentation. By creating an account or accessing the Service, you agree to them."
      stats={[
        { value: "16", label: "SECTIONS" },
        { value: "30d", label: "TERMINATION NOTICE" },
        { value: "90d", label: "POST-EXIT EXPORT WINDOW" },
        { value: "$1k", label: "DEFAULT LIABILITY CAP FLOOR" },
      ]}
      pillars={PILLARS}
      sections={SECTIONS}
    >
      <div className="qt-legal-callout">
        <strong>Placeholders.</strong> Before going live, replace
        <code>{"{COMPANY_ENTITY}"}</code>, <code>{"{COMPANY_ADDRESS}"}</code>, and
        <code>{"{GOVERNING_LAW_STATE}"}</code> with your registered legal entity,
        principal place of business, and the U.S. state whose laws govern this
        agreement (e.g. Delaware). Have these reviewed by counsel.
      </div>

      <p>
        These Terms of Service (the &ldquo;<strong>Terms</strong>&rdquo;) form a
        binding agreement between you (&ldquo;<strong>you</strong>&rdquo; or
        &ldquo;<strong>Customer</strong>&rdquo;) and{" "}
        <strong>{"{COMPANY_ENTITY}"}</strong>, the operator of QorTrace
        (&ldquo;<strong>QorTrace</strong>&rdquo;, &ldquo;<strong>we</strong>&rdquo;,
        &ldquo;<strong>us</strong>&rdquo;), governing your access to and use of
        the QorTrace web application, APIs, command-line tools, and related
        documentation (collectively, the &ldquo;<strong>Service</strong>&rdquo;).
      </p>
      <p>
        By creating an account, accessing the Service, or clicking
        &ldquo;Accept,&rdquo; you agree to these Terms. If you do not agree, do
        not use the Service.
      </p>

      <h2 id="service">1. The Service</h2>
      <p>
        QorTrace provides post-quantum cryptographic risk intelligence for
        blockchain infrastructure, including wallet exposure scanning, smart
        contract auditing, signed security certificates, embeddable verified
        badges, and continuous monitoring tooling. The Service is provided on a
        subscription, metered, or one-off engagement basis as described on our{" "}
        <a href="/pricing">pricing pages</a>.
      </p>
      <p>
        <strong>Reports are advisory.</strong> QorTrace findings, scores, and
        certificates reflect our best technical assessment under our published{" "}
        <a href="/methodology">methodology</a>. They do not constitute legal,
        investment, or fiduciary advice and should not be relied upon as the
        sole basis for any operational, financial, or compliance decision.
      </p>

      <h2 id="eligibility">2. Eligibility and Accounts</h2>
      <p>
        You must be at least 18 years old and capable of forming a binding
        contract under the laws of your jurisdiction. You agree to provide
        accurate, current, and complete information when registering and to
        keep credentials confidential. You are responsible for all activity
        under your account, including activity by team members you invite.
      </p>

      <h2 id="acceptable-use">3. Acceptable Use</h2>
      <p>You agree not to, and not to allow any third party to:</p>
      <ul>
        <li>
          Submit source code, contracts, or wallet addresses for which you do
          not have the necessary rights or authorization.
        </li>
        <li>
          Reverse-engineer, decompile, or attempt to extract proprietary
          methodology, scoring weights, or model prompts beyond what we publish.
        </li>
        <li>
          Use the Service to facilitate unlawful activity, including sanctions
          evasion, money laundering, or unauthorized access to third-party
          systems.
        </li>
        <li>
          Resell, sublicense, or white-label outputs of the Service without a
          written agreement with us.
        </li>
        <li>
          Probe, scan, or test the vulnerability of the Service or attempt to
          breach security or authentication measures (see our{" "}
          <a href="/legal/security">Responsible Disclosure</a> page for the
          authorized path).
        </li>
        <li>
          Generate automated traffic in excess of documented rate limits or
          interfere with other customers&rsquo; use of the Service.
        </li>
      </ul>

      <h2 id="customer-content">4. Customer Content; Audit Reports</h2>
      <p>
        &ldquo;<strong>Customer Content</strong>&rdquo; means source code,
        wallet addresses, project metadata, logo files, and other materials you
        submit to the Service. You retain all right, title, and interest in
        Customer Content. You grant us a worldwide, non-exclusive, royalty-free
        license to host, process, and analyze Customer Content solely to
        provide and improve the Service.
      </p>
      <p>
        Audit reports, certificates, scores, and findings produced for you on
        delivered audits are licensed to you under these Terms; you may
        reproduce and distribute them in their unmodified form for legitimate
        business purposes, including investor decks, compliance bundles, and
        public verification pages.
      </p>

      <h2 id="ip">5. Intellectual Property</h2>
      <p>
        The Service, our methodology, models, scoring rubric, code, designs,
        and trademarks (including the QorTrace name and Q-mark) are and remain
        our exclusive property and that of our licensors. No license is granted
        except as expressly stated in these Terms.
      </p>

      <h2 id="fees-billing">6. Fees, Billing, and Refunds</h2>
      <p>
        Paid plans are billed via our payment processor, Stripe, in advance on
        a monthly or annual cycle as selected at checkout. One-off audit fees
        are charged at order placement. All fees are exclusive of applicable
        taxes, which will be added where required.
      </p>
      <p>
        We do not provide refunds for partial billing periods, unused seats,
        or completed audit deliverables. We will, in our reasonable discretion,
        credit your account for verifiable Service outages of more than 24
        consecutive hours.
      </p>

      <h2 id="term-termination">7. Term, Suspension, and Termination</h2>
      <p>
        These Terms remain in effect while you use the Service. Either party
        may terminate at any time. We may suspend or terminate your access
        immediately, with or without notice, if we reasonably believe you have
        violated these Terms, applicable law, or have created a security or
        operational risk to other customers. On termination, your right to use
        the Service ends; provisions that by their nature should survive
        (including IP, indemnification, disclaimers, and limitations of
        liability) will survive.
      </p>

      <h2 id="confidentiality">8. Confidentiality</h2>
      <p>
        Each party agrees to protect the other&rsquo;s non-public information
        with reasonable care and to use it only to perform under these Terms.
        Customer Content is treated as Customer&rsquo;s confidential
        information.
      </p>

      <h2 id="warranties">9. Warranties and Disclaimers</h2>
      <p>
        We warrant that we will provide the Service with reasonable skill and
        care and in accordance with our published methodology.{" "}
        <strong>
          Except as expressly stated above, the Service is provided &ldquo;as
          is&rdquo; and &ldquo;as available&rdquo; without warranty of any
          kind, express or implied, including warranties of merchantability,
          fitness for a particular purpose, non-infringement, accuracy of
          findings, or that the Service will be uninterrupted, error-free, or
          secure.
        </strong>
      </p>

      <h2 id="liability">10. Limitation of Liability</h2>
      <p>
        To the maximum extent permitted by law, neither party will be liable
        for any indirect, incidental, special, consequential, exemplary, or
        punitive damages, or for lost profits, lost revenue, lost data, or
        cost of substitute services, whether based on contract, tort, strict
        liability, or any other theory, even if advised of the possibility.
        Our aggregate liability arising out of or relating to these Terms or
        the Service will not exceed the greater of (a) the fees you paid us
        in the twelve (12) months preceding the event giving rise to the claim
        or (b) US&nbsp;$100.
      </p>

      <h2 id="indemnification">11. Indemnification</h2>
      <p>
        You agree to indemnify, defend, and hold harmless QorTrace and its
        affiliates, officers, employees, and agents from any third-party claim
        arising from your Customer Content, your use of the Service in
        violation of these Terms, or your violation of any law or third-party
        right.
      </p>

      <h2 id="third-party">12. Third-Party Services</h2>
      <p>
        The Service integrates with third-party providers (e.g. Stripe for
        payments, Resend for transactional email, Cloudflare for delivery).
        Your use of those services is governed by their own terms; we are
        not responsible for their acts or omissions.
      </p>

      <h2 id="changes-service">13. Changes to the Service or Terms</h2>
      <p>
        We may modify the Service and these Terms from time to time. Material
        changes will be communicated by email or via the Service at least
        seven (7) days before they take effect. Continued use after the
        effective date constitutes acceptance.
      </p>

      <h2 id="governing-law">14. Governing Law and Disputes</h2>
      <p>
        These Terms are governed by the laws of the State of{" "}
        <strong>{"{GOVERNING_LAW_STATE}"}</strong>, USA, without regard to
        conflict-of-laws rules. The parties consent to the exclusive
        jurisdiction of the state and federal courts located in{" "}
        <strong>{"{GOVERNING_LAW_STATE}"}</strong> for any dispute that is not
        subject to arbitration.
      </p>

      <h2 id="misc">15. Miscellaneous</h2>
      <p>
        These Terms are the entire agreement between the parties on this
        subject. If any provision is found unenforceable, the remainder will
        remain in effect. Neither party&rsquo;s failure to enforce any right
        is a waiver. You may not assign these Terms without our prior written
        consent; we may assign in connection with a merger, acquisition, or
        sale of assets.
      </p>

      <h2 id="contact">16. Contact</h2>
      <p>
        Questions about these Terms? Contact us at{" "}
        <a href="mailto:legal@qortrace.com">legal@qortrace.com</a> or write to{" "}
        <strong>{"{COMPANY_ENTITY}, {COMPANY_ADDRESS}"}</strong>.
      </p>
    </LegalLayout>
  );
}
